import { Component, ComponentFactoryResolver, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ILocationService } from "angular";
import { DisplayNote } from "ng/src/app/notes/note-display/note-display.model";
import { Note } from "ng/src/app/notes/shared/note.model";
import { PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { CommentListService } from "ng/src/app/shared/comment-list/comment-list.service";
import { CommentEntity } from "ng/src/app/shared/comment-list/comment-list.model";
import { finalize, take, takeUntil } from "rxjs/operators";
import { NoteAction, NoteActionType } from "ng/src/app/notes/note-actions/note-actions.model";
import { PersistentDialogController } from "ng/src/app/shared/persistent-dialog/persistent-dialog.model";
import { Subject } from "rxjs";
import { NoteDetailComponent } from "ng/src/app/notes/note-detail/note-detail.component";


@Component({
	selector: 'lb-note-actions[note]',
	providers: [CommentListService],
	template: `
        <div class="p-d-flex">
            <ng-container *ngFor="let action of actions">
	            <ng-container *ngIf="shouldDisplayAction(action)">
	                <button pButton class="p-mr-1 p-button-outlined p-button-rounded"
	                        [title]="getLabel(action)" [icon]="getIcon(action)" (middleclick)="performAction(action.type, $event)"
	                        (click)="performAction(action.type, $event)">
	                </button>
	            </ng-container>
            </ng-container>
        </div>
        <ng-template #commentTemplate>
	        <textarea pInputTextarea autofocus [style]="{width: '100%', maxHeight: '60vh', minHeight: '100px', resize: 'vertical'}" [(ngModel)]="comment.comment"
	                  placeholder="Enter Comment"></textarea>
	        <div class="p-d-flex p-jc-end">
		        <button pButton [tabindex]="2" class="p-button-secondary p-mr-1" [disabled]="saving" label="Cancel" (click)="cancel()"></button>
		        <button pButton [tabindex]="1" label="Submit" [disabled]="saving || commentLength <= 0" (click)="save()"></button>
	        </div>
        </ng-template>
        <ng-template #headerDetailTemplate>
	        <div class="p-d-flex p-jc-between p-ai-center w-100">
		        <span class="p-dialog-title">Note Detail</span>
		        <button pButton class="p-button-text p-button-secondary p-button-lg p-button-rounded" icon="pi pi-times" (click)="cancel()"></button>
	        </div>
        </ng-template>
	`,
})
export class NoteActionsComponent implements OnInit, OnDestroy {
	@Input() note!: DisplayNote<Note>;
	
	@Input()
	actions: NoteAction[] = NoteAction.defaultSet();
	
	// Takes a function instead of going through Angular output due to output event emitter not working if
	// the component is destroyed
	@Input()
	completion?: ((type: NoteActionType, note: DisplayNote<Note>) => void);
	
	@ViewChild('commentTemplate') commentTemplate!: TemplateRef<any>;
	@ViewChild('headerDetailTemplate') headerDetailTemplate!: TemplateRef<any>;
	
	get commentLength() {
		return (this.comment?.comment?.length ?? 0);
	}
	
	constructor(
		private dialogService: PersistentDialogService,
		private commentService: CommentListService,
		private compFactory: ComponentFactoryResolver,
		@Inject('$location')
		private $location: ILocationService
	) {}
	
	comment = CommentEntity.newComment();
	saving = false;
	
	noteDetailControl?: PersistentDialogController<NoteDetailComponent>;
	
	private readonly destroySubject = new Subject();
	readonly destroy$ = this.destroySubject.asObservable();
	
	ngOnInit(): void {
		this.commentService.setup(this.note);
	}
	
	ngOnDestroy(): void {
		this.destroySubject.next();
	}
	
	getLabel(action: NoteAction) {
		return typeof action.label === 'string' ? action.label : action.label(this.note);
	}
	
	getIcon(action: NoteAction) {
		return typeof action.icon === 'string' ? action.icon : action.icon(this.note);
	}
	
	shouldDisplayAction(action: NoteAction) {
		return typeof action.display === 'boolean' ? action.display : (action.display?.(this.note) ?? true);
	}
	
	performAction(type: NoteActionType, $event: MouseEvent) {
		switch (type) {
			case "COMMENT":
				this.dialogService.next({
					template: this.commentTemplate,
					settings: {
						closeable: false,
						header: 'New Comment'
					}
				});
				this.comment = CommentEntity.newComment();
				break;
			case "DETAIL":
				if ($event.button === 1 || $event.ctrlKey || $event.shiftKey || $event.metaKey) {
					window.open(`/#/note-detail?log=${this.note.logId}&noteId=${this.note.id}`);
					this.complete(type);
				}
				else {
					this.noteDetailControl = this.getDetailSetup()
						.setOnDialogClose(() => this.complete(type));
					this.dialogService.next(this.noteDetailControl);
				}
				break;
		}
	}
	
	complete(type: NoteActionType) {
		this.completion?.(type, this.note);
	}
	
	save() {
		this.saving = true;
		this.commentService.addComment(this.comment).pipe(
			finalize(() => this.saving = false)
		).subscribe(() => {
			this.dialogService.close();
			this.complete('COMMENT');
		});
	}
	
	cancel() {
		this.dialogService.close();
	}
	
	private getDetailSetup(): PersistentDialogController<NoteDetailComponent> {
		return new PersistentDialogController(NoteDetailComponent)
			.setRespondToServiceClose(true)
			.setCloseOnNavigation(true)
			.setCloseOnEscape(true)
			.setOnDialogCreated((dialog) => {
				dialog.draggable = false;
				dialog.headerTemplate = this.headerDetailTemplate;
				dialog.breakpoints = {'1000px': '100%'} as any;
				dialog.style = { width: '90%'};
				dialog.position = 'top';
				dialog.closable = false;
				dialog.resizable = false;
				dialog.modal = true;
				dialog.onHide.pipe(
						takeUntil(this.destroy$),
						take(1)
					)
					.subscribe(() => this.complete('DETAIL'));
		})
			.setOnComponentCreated((ref) => {
				const component = ref.instance;
				component.queryPagerEnabled = false;
				component.detailOnly = true;
				component.noteDetailId = this.note.id;
			});
	}
}